import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { storage } from '@uhg-abyss/web/tools/storage';
import { ProviderSelectDrawer } from '@sde/widgets-core/src/widgets/ProviderSelectDrawer';
import { event } from '@uhg-abyss/web/tools/event';
import { useModal } from 'src/state/modal';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { useDashboard } from 'src/state/dashboard';

const permissionsStorageKey = 'provider-permissions';

export const ProviderSelectDrawerWidget = ({
  provider,
  setProvider,
  isProviderOpen,
  setDigitalSecurityError,
  toggleProvider,
  setProviderNotSaved,
}) => {
  const { user } = useUser.state();
  const { updateUser } = useUser.actions();
  const { modals } = useModal.state();
  const { updateProviderPreferenceError } = useDashboard.actions();

  useEffect(() => {
    const cachedPermissions = storage.local.get(permissionsStorageKey);
    if (cachedPermissions?.length > 0) {
      updateUser({ permissions: cachedPermissions });
    }
  }, []);

  const changeCareProviderStartEvent = () => {
    event('CHANGE_CARE_PROVIDER_START');
  };

  const widgetProps = {
    data: {
      title: '',
      user,
      provider,
      confirmSelection: !!modals['documents-search'],
      isProviderOpen,
      toggleProvider,
      saveDisabled: user.isTechnicalUser, // if user is emulating, save is disabled
    },
    events: {
      onDrawerClose: () => {
        toggleProvider(false);
      },
      onChange: (data) => {
        event.setData({
          corporateProviderName:
            data.corporateProviderName || data.provider?.corporateProviderName,
          taxIdNumber: data.taxIdNumber || data.provider?.taxIdNumber,
          corporateProviderMpin:
            data.corporateProviderMpin || data.provider?.corporateProviderMpin,
          careProviderMpin:
            data.servicingProviderMpin || data.provider?.servicingProviderMpin,
          careProviderName:
            data.servicingProviderName || data.provider?.servicingProviderName,
          careProviderFirstName:
            data.servicingProviderFirstName ||
            data.provider?.servicingProviderFirstName,
          careProviderLastName:
            data.servicingProviderLastName ||
            data.provider?.servicingProviderLastName,
          allPermission: data.allPermission || false,
          permissionCode: data.permissionCode || [],
          tpsm: data.provider?.tpsm || [],
        });
        setProviderNotSaved(false);
        storage.local.set(permissionsStorageKey, data.permissions);

        if (
          data?.permissions?.length > 0 ||
          data?.permissionSubCode?.length > 0 ||
          data?.permissionCode?.length > 0 ||
          data?.allPermission
        ) {
          setProvider({
            ...data.provider,
            permissions: data.permissions || [],
            privileges: data.organizations || [],
            allPermission: data.allPermission,
            permissionSubCode: data.permissionSubCode || [],
            permissionCode: data.permissionCode || [],
          });
          updateUser({
            permissions: data.permissions || [],
            privileges: data.organizations || [],
            allPermission: data.allPermission,
            permissionSubCode: data.permissionSubCode || [],
            permissionCode: data.permissionCode || [],
            provider: data.provider,
          });
        } else {
          setProvider(data.provider);
        }
      },
      onSave: () => {
        toggleProvider(false);
        setDigitalSecurityError({
          isDigitalSecurityError: false,
          isProviderPreferenceError: false,
        });
        event('UPDATE_PROVIDER_SELECTION');
        event('CHANGE_CARE_PROVIDER_COMPLETE');
        event('CHANGE_CARE_PROVIDER_SAVE');
        updateProviderPreferenceError({});
      },
      onCancel: () => {
        toggleProvider(false);
      },
      onError: () => {
        storage.local.remove(permissionsStorageKey);
        setProviderNotSaved(true);
      },
      onOrganizationChange: changeCareProviderStartEvent,
      onCorporateChange: changeCareProviderStartEvent,
      onTaxIdNumberChange: changeCareProviderStartEvent,
      onCareProviderChange: changeCareProviderStartEvent,
      onSaveCorporates: (data) => {
        if (data.length > 0) {
          updateUser({
            corporates: data,
          });
        }
      },
      onDigitalSecurityError: (err) => {
        updateUser({
          code: err.code,
        });
      },
    },
  };

  return <ProviderSelectDrawer {...widgetProps} />;
};

ProviderSelectDrawerWidget.propTypes = {
  provider: PropTypes.shape({}).isRequired,
  toggleProvider: PropTypes.func.isRequired,
  setProvider: PropTypes.func.isRequired,
  isProviderOpen: PropTypes.bool.isRequired,
  setDigitalSecurityError: PropTypes.func.isRequired,
  setProviderNotSaved: PropTypes.func.isRequired,
};
